import { Controller } from "@hotwired/stimulus"

// Field-level inline editing for a round.
//
// Each field is its own request. Click the value, it becomes an input; blur
// commits, Enter commits, Escape reverts. Never mid-keystroke — a half-typed
// server URL must never reach the server — and never a page-level save button,
// so the status can live in the field's own row.
//
// Because each field commits independently, two organizers editing the same
// round at once is harmless: last blur wins per field, and the loser simply
// sees the other value on their next render. No locks, nothing implying
// exclusivity.
//
// The date field additionally warns *before* commit when the new time would
// move the round past a neighbour, because the round number is derived from
// date order and the organizer can't otherwise see the consequence. The
// neighbouring dates ship with the page (they're already loaded) rather than
// being fetched on focus.
export default class extends Controller {
  static targets = ["field", "display", "input", "editor", "hint", "status"]
  static values = {
    url: String,
    siblings: { type: Array, default: [] },
    position: Number,
  }

  edit(event) {
    const field = event.currentTarget.closest(
      "[data-inline-field-target='field']",
    )
    const editor = this._editor(field)

    // A picker field (track) puts a floating panel behind the value rather
    // than an input in the row, so the value stays on screen: hiding it would
    // leave the row blank under a panel that covers something else entirely.
    // The panel loads lazily, so the first open only has to reveal the frame
    // and it opens itself; later opens ask it to.
    if (editor.querySelector("turbo-frame")) {
      this._show(field, true, { keepDisplay: true })
      editor.querySelector("[data-controller~='track-picker']")?.dispatchEvent(
        new CustomEvent("track-picker:openRequest", {
          detail: { anchor: event.currentTarget },
        }),
      )
      return
    }

    this._show(field, true)

    const input = this._input(field)
    input.focus()
    if (input.type !== "datetime-local") input.select()
    this._renderHint(field)
  }

  key(event) {
    const field = event.currentTarget.closest(
      "[data-inline-field-target='field']",
    )
    if (event.key === "Enter") {
      event.preventDefault()
      event.currentTarget.blur() // blur commits; don't double-submit
    } else if (event.key === "Escape") {
      event.preventDefault()
      this._revert(field)
    }
  }

  // Live, pre-commit: the renumber warning has to be visible while you're
  // still deciding, not after the schedule has already reordered.
  preview(event) {
    this._renderHint(
      event.currentTarget.closest("[data-inline-field-target='field']"),
    )
  }

  async commit(event) {
    const field = event.currentTarget.closest(
      "[data-inline-field-target='field']",
    )
    if (field.dataset.reverting === "true") {
      delete field.dataset.reverting
      return
    }

    const input = this._input(field)
    const name = field.dataset.fieldName
    // `defaultValue` is the dirty check for a text input, but NOT for the
    // picker's <input type="hidden">: for hidden inputs the value IDL attribute
    // *reflects* the content attribute, so assigning `.value` also rewrites
    // `defaultValue` and the two can never differ. A field that knows its
    // server-rendered value states it explicitly.
    const was = field.dataset.originalValue ?? input.defaultValue
    if (input.value === was) return this._show(field, false)

    // One save per field at a time. A single edit can raise more than one
    // committing event — blur and change both fire when a control is closed by
    // choosing from it — and each would PATCH the same value. Idempotent on the
    // server, but it doubles the writes and races two re-renders of the frame
    // this field lives in.
    if (field.dataset.saving === "true") return
    field.dataset.saving = "true"

    this._status(field, "Saving…", "var(--app-text-muted)")

    try {
      const body = new FormData()
      body.append(`round[${name}]`, input.value)
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token":
            document.querySelector("meta[name='csrf-token']")?.content ?? "",
          Accept: "text/vnd.turbo-stream.html, text/html",
        },
        body,
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      // The server owns the rendering — a saved date changes the displayed
      // format, the round's number, and possibly its neighbours' — so take the
      // stream response rather than patching the DOM from the input value.
      const html = await response.text()
      if (html.includes("<turbo-stream")) {
        window.Turbo.renderStreamMessage(html)
      } else {
        this._commitLocally(field, input)
      }
      this._status(field, "Saved", "var(--color-live)")
      setTimeout(() => this._status(field, "", ""), 2000)
    } catch (_error) {
      this._status(field, "Retry", "#ef4444")
      field
        .querySelector("[data-inline-field-target='input']")
        ?.classList.add("border-red-500")
    } finally {
      // The frame usually re-renders this element away; clearing matters for
      // the failure path, where the organizer stays on the field to retry.
      delete field.dataset.saving
    }
  }

  // ───── internals ─────

  _commitLocally(field, input) {
    input.defaultValue = input.value
    const display = field.querySelector(
      "[data-inline-field-target='display'] span",
    )
    if (display) display.textContent = input.value
    this._show(field, false)
  }

  _revert(field) {
    const input = this._input(field)
    input.value = input.defaultValue
    field.dataset.reverting = "true"
    this._show(field, false)
    this._status(field, "", "")
    input.blur()
  }

  _show(field, editing, { keepDisplay = false } = {}) {
    this._editor(field).classList.toggle("hidden", !editing)
    field
      .querySelector("[data-inline-field-target='display']")
      .classList.toggle("hidden", editing && !keepDisplay)
    const hint = field.querySelector("[data-inline-field-target='hint']")
    if (hint) hint.classList.toggle("hidden", !editing)
  }

  _input(field) {
    return field.querySelector("[data-inline-field-target='input']")
  }

  // What gets shown and hidden. Usually the input itself; for a picker it is
  // the wrapper around the widget, because hiding the widget's hidden value
  // input would leave the list on screen.
  _editor(field) {
    return (
      field.querySelector("[data-inline-field-target='editor']") ||
      this._input(field)
    )
  }

  _status(field, text, color) {
    const el = field.querySelector("[data-inline-field-target='status']")
    if (!el) return
    el.textContent = text
    el.style.color = color
  }

  // "Moving this past R6 (21 May) renumbers the schedule — this becomes R6."
  _renderHint(field) {
    const hint = field.querySelector("[data-inline-field-target='hint']")
    if (!hint) return
    if (field.dataset.fieldName !== "scheduled_at") return

    const value = this._input(field).value
    const base = "Enter saves · Esc cancels"
    if (!value) return this._setHint(hint, base, "var(--app-text-faint)")

    const at = new Date(value)
    const earlier = this.siblingsValue.filter((s) => new Date(s.at) < at).length
    const next = earlier + 1

    if (next === this.positionValue)
      return this._setHint(hint, base, "var(--app-text-faint)")

    this._setHint(
      hint,
      `Moving this renumbers the schedule — this becomes R${next}.`,
      "#d97706",
    )
  }

  _setHint(hint, text, color) {
    hint.textContent = text
    hint.style.color = color
  }
}
